import { useState, useEffect } from "react";
import dayjs from "dayjs";

// ==========================================
// HELPERS INTERNOS DO HOOK
// ==========================================
const getDefaultDates = () => {
	const final = new Date();
	final.setHours(0, 0, 0, 0);

	const inicial = new Date(final);
	inicial.setDate(inicial.getDate() - 7);

	return { inicial, final };
};

// ==========================================
// HOOK PRINCIPAL
// ==========================================
export default function useGenerateReportForm({
	api,
	clienteInfo,
	allHostsCliente,
	getInterface,
	setSnackbar,
	reportToEdit,
}) {
	// ERP do cliente selecionado (a lista de clientes traz `erp`).
	const erp = clienteInfo?.erp?.tipo || "";
	const labelErp = erp === "VOALLE" || erp === "IXC" ? "Contrato" : "Serviço";

	const defaultDates = getDefaultDates();

	// ==========================================
	// ESTADOS
	// ==========================================
	const [selectedTypes, setSelectedTypes] = useState([]);
	const [prevSelectedTypes, setPrevSelectedTypes] = useState([]);
	const [rules, setRules] = useState([]);
	const [selectors, setSelectors] = useState([
		{ id: Date.now(), hostId: "", interfaces: [], availableInterfaces: [] },
	]);
	const [allServiceClient, setAllServiceClient] = useState([]);
	const [loadingServices, setLoadingServices] = useState(false);
	const [servicoHostOnus, setServicoHostOnus] = useState([
		{ servico: null, host: null, onu: null },
	]);
	const [periodoTipo, setPeriodoTipo] = useState("Semanal");
	const [diasPersonalizado, setDiasPersonalizado] = useState("");
	const [selectedAtendimentosServices, setSelectedAtendimentosServices] =
		useState([]);
	const [atendimentosInputValue, setAtendimentosInputValue] = useState("");
	const [atendimentosIsOpen, setAtendimentosIsOpen] = useState(false);
	const [atendimentosSortedOptions, setAtendimentosSortedOptions] = useState(
		[],
	);
	const [nomeRelatorio, setNomeRelatorio] = useState("");
	const [dataInicial, setDataInicial] = useState(defaultDates.inicial);
	const [dataFinal, setDataFinal] = useState(defaultDates.final);
	const [openModal, setOpenModal] = useState(false);
	const [tarifacaoValores, setTarifacaoValores] = useState({
		valorPorMbps: "",
		planoContratadoMbps: "",
		valorMbpsExcedente: "",
	});
	const [metrics, setMetrics] = useState({
		trafego: false,
		tarifacao: false,
		disponibilidade: false,
		perdaPacote: false,
		latencia: false,
	});
	const [isAgendamentoEnabled, setIsAgendamentoEnabled] = useState(false);
	const [agendamentoDataInicial, setAgendamentoDataInicial] = useState(null);
	const [emails, setEmails] = useState([{ id: Date.now(), value: "" }]);
	const [frequenciaAgendamento, setFrequenciaAgendamento] = useState("Único");
	const [observacoes, setObservacoes] = useState("");

	// ==========================================
	// VALORES DERIVADOS (computed)
	// ==========================================
	const showMetricas = selectedTypes.some((type) =>
		["CPE", "ONU", "EasyFlow"].includes(type),
	);

	const showTarifacao =
		selectedTypes.includes("CPE") || selectedTypes.includes("EasyFlow");

	const showDisponibilidade =
		selectedTypes.includes("CPE") || selectedTypes.includes("ONU");

	const allSelectedAtendimentos =
		selectedAtendimentosServices?.length === allServiceClient?.length &&
		allServiceClient?.length > 0;

	const displayedAtendimentosValue =
		selectedAtendimentosServices?.length > 1
			? `${selectedAtendimentosServices.length} ${labelErp}s selecionados`
			: selectedAtendimentosServices?.[0]
				? `${selectedAtendimentosServices[0]?.id_cliente_servico} - ${selectedAtendimentosServices[0]?.nome}`
				: "";

	// ==========================================
	// EFFECTS
	// ==========================================
	useEffect(() => {
		setMetrics((prev) => {
			let updated = { ...prev };
			let changed = false;

			if (!showMetricas) {
				if (Object.values(prev).some(Boolean)) {
					updated = {
						trafego: false,
						tarifacao: false,
						disponibilidade: false,
						perdaPacote: false,
						latencia: false,
					};
					changed = true;
				}
			} else {
				if (!showTarifacao && (prev.tarifacao || prev.trafego)) {
					updated.tarifacao = false;
					updated.trafego = false;
					changed = true;
				}
				if (!showDisponibilidade && prev.disponibilidade) {
					updated.disponibilidade = false;
					changed = true;
				}
			}

			return changed ? updated : prev;
		});
	}, [showMetricas, showTarifacao, showDisponibilidade]);

	// ==========================================
	// EFFECT: CARREGAR DADOS DO REPORT_TO_EDIT
	// ==========================================
	useEffect(() => {
		if (!reportToEdit) return;

		setNomeRelatorio(reportToEdit.nome || "");
		setObservacoes(reportToEdit.observacoes || "");

		if (reportToEdit.dataInicial) setDataInicial(new Date(reportToEdit.dataInicial));
		if (reportToEdit.dataFinal) setDataFinal(new Date(reportToEdit.dataFinal));

		if (reportToEdit.tipos?.length) {
			setSelectedTypes(
				reportToEdit.tipos.map((t) =>
					t.tipo === "ATENDIMENTO" ? "Atendimentos" : t.tipo
				)
			);
		}

		if (reportToEdit.metricas?.length) {
			const activeMetrics = {};
			reportToEdit.metricas.forEach((m) => {
				const met = m.metrica.toLowerCase();
				if (met === "trafego") activeMetrics.trafego = true;
				if (met === "tarifacao") activeMetrics.tarifacao = true;
				if (met === "disponibilidade") activeMetrics.disponibilidade = true;
				if (met === "perdapacote") activeMetrics.perdaPacote = true;
				if (met === "latencia") activeMetrics.latencia = true;
			});
			setMetrics((prev) => ({ ...prev, ...activeMetrics }));
		}

		if (reportToEdit.motorDeRegras?.length) {
			setRules(
				reportToEdit.motorDeRegras.map((r, i) => ({
					id: Date.now() + i,
					metric: r.metrica === "PERDAPACOTE" ? "packetLoss" : r.metrica.toLowerCase(),
					operator: r.operador,
					value: r.valor,
				}))
			);
		} else if (reportToEdit) {
			setRules([]);
		}

		if (reportToEdit.tarifacao) {
			setTarifacaoValores({
				valorPorMbps: reportToEdit.tarifacao.valorPorMbps || "",
				planoContratadoMbps: reportToEdit.tarifacao.planoContratadoMbps || "",
				valorMbpsExcedente: reportToEdit.tarifacao.valorMbpsExcedente || "",
			});
		}

		if (reportToEdit.agendamento) {
			setIsAgendamentoEnabled(true);
			setFrequenciaAgendamento(
				reportToEdit.agendamento.frequencia.charAt(0).toUpperCase() + reportToEdit.agendamento.frequencia.slice(1).toLowerCase()
			);
			setAgendamentoDataInicial(new Date(reportToEdit.agendamento.dataInicial));
			if (reportToEdit.agendamento.tipoPeriodo) {
				setPeriodoTipo(
					reportToEdit.agendamento.tipoPeriodo.charAt(0).toUpperCase() + reportToEdit.agendamento.tipoPeriodo.slice(1).toLowerCase()
				);
			}
			if (reportToEdit.agendamento.diasPersonalizado) {
				setDiasPersonalizado(reportToEdit.agendamento.diasPersonalizado);
			}
			if (reportToEdit.agendamento.emails?.length) {
				setEmails(
					reportToEdit.agendamento.emails.map((email, i) => ({
						id: Date.now() + i,
						value: email,
					}))
				);
			}
		}

		if (reportToEdit.relatorioCPE?.hosts?.length) {
			const hostsArray = reportToEdit.relatorioCPE.hosts;
			const initialSelectors = hostsArray.map((host, i) => ({
				id: Date.now() + i,
				hostId: host.zabbixHostId,
				interfaces: host.interfaces?.map((interf) => interf.nome) || [],
				availableInterfaces: [],
			}));
			setSelectors(initialSelectors);
		}
	}, [reportToEdit]);

	useEffect(() => {
		if (reportToEdit) return;

		if (!selectedTypes.includes("CPE")) {
			setSelectors([
				{ id: Date.now(), hostId: "", interfaces: [], availableInterfaces: [] },
			]);
		}
		if (!selectedTypes.includes("ONU")) {
			setServicoHostOnus([{ servico: null, host: null, onu: null }]);
		}
		if (!selectedTypes.includes("Atendimentos")) {
			setSelectedAtendimentosServices([]);
		}
	}, [selectedTypes, reportToEdit]);

	useEffect(() => {
		if (reportToEdit) return;

		const addedCpe = selectedTypes.includes("CPE") && !prevSelectedTypes.includes("CPE");
		const addedOnu = selectedTypes.includes("ONU") && !prevSelectedTypes.includes("ONU");

		if (addedCpe || addedOnu) {
			setMetrics((prev) => {
				const updated = { ...prev };
				if (addedCpe) {
					updated.trafego = true;
					updated.disponibilidade = true;
					updated.perdaPacote = true;
					updated.latencia = true;
				}
				if (addedOnu) {
					updated.disponibilidade = true;
					updated.perdaPacote = true;
					updated.latencia = true;
				}
				return updated;
			});
		}

		setPrevSelectedTypes(selectedTypes);
	}, [selectedTypes, prevSelectedTypes, reportToEdit]);

	useEffect(() => {
		const needsServices =
			selectedTypes.includes("ONU") || selectedTypes.includes("Atendimentos");

		if (needsServices && clienteInfo?.id && !allServiceClient?.length) {
			getAllServicesClient();
		}
	}, [selectedTypes, clienteInfo]);

	useEffect(() => {
		if (reportToEdit && reportToEdit.nome) return;
		if (!clienteInfo) return;
		const nomeSugerido = clienteInfo?.nome;
		setNomeRelatorio((prev) => prev || nomeSugerido);
	}, [clienteInfo, reportToEdit]);

	useEffect(() => {
		if (!allServiceClient || !allServiceClient.length) return;

		setAtendimentosSortedOptions(
			getSortedOptions(
				[
					...(selectedAtendimentosServices ?? []),
					...allServiceClient,
				],
				selectedAtendimentosServices,
			),
		);
	}, [allServiceClient, selectedAtendimentosServices]);

	useEffect(() => {
		if (
			selectedTypes.includes("Atendimentos") &&
			Array.isArray(allServiceClient) &&
			allServiceClient.length > 0 &&
			selectedAtendimentosServices.length === 0
		) {
			if (reportToEdit?.relatorioAtendimentos?.length) {
				const ids = reportToEdit.relatorioAtendimentos.map((a) => String(a.contratoId));
				const restored = allServiceClient.filter((s) => ids.includes(String(s.id_cliente_servico)));
				setSelectedAtendimentosServices(restored);
			} else {
				setSelectedAtendimentosServices([...allServiceClient]);
			}
		}
	}, [selectedTypes, allServiceClient, reportToEdit]);

	useEffect(() => {
		if (
			reportToEdit &&
			reportToEdit.relatorioONU?.onus?.length &&
			allServiceClient?.length &&
			allHostsCliente?.length &&
			servicoHostOnus.length === 1 &&
			!servicoHostOnus[0].onu
		) {
			const combined = generateCombinedOptions();
			const restoredOnus = reportToEdit.relatorioONU.onus.map(onuItem => {
				const match = combined.find(c => c.onu.onuItem === onuItem.zabbixItemId);
				if (match) {
					return { servico: match.servico, host: match.host, onu: match.onu };
				}
				return { servico: null, host: null, onu: { onuItem: onuItem.zabbixItemId } };
			});
			setServicoHostOnus(restoredOnus);
		}
	}, [reportToEdit, allServiceClient, allHostsCliente]);

	// ==========================================
	// SERVIÇOS
	// ==========================================
	const getAllServicesClient = async () => {
		if (!api) return;
		try {
			setLoadingServices(true);
			// O backend acha o cliente pelo clienteId e consulta o ERP dele (o
			// código do cliente pode se repetir em outro ERP).
			const body = { clienteId: clienteInfo?.id };
			const response = await api.post("/interno/find/allServiceClient", body);
			setAllServiceClient(response?.data ?? []);
		} catch (error) {
			setSnackbar?.({
				children: "Error: Não foi possível buscar os serviços",
				severity: "error",
			});
			console.error(error);
		} finally {
			setLoadingServices(false);
		}
	};

	// ==========================================
	// EMAILS
	// ==========================================
	const handleAddEmail = () =>
		setEmails((prev) => [...prev, { id: Date.now(), value: "" }]);

	const handleRemoveEmail = (id) =>
		setEmails((prev) => prev.filter((email) => email.id !== id));

	const handleEmailChange = (id, value) =>
		setEmails((prev) =>
			prev.map((email) => (email.id === id ? { ...email, value } : email)),
		);

	// ==========================================
	// TIPOS DE RELATÓRIO
	// ==========================================
	const handleToggle = (id) => {
		if (!selectedTypes.includes(id) && !clienteInfo?.id) {
			setSnackbar?.({
				severity: "warning",
				children: "É necessário selecionar um cliente.",
			});
			return;
		}
		setSelectedTypes((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
		);
	};

	// ==========================================
	// ONU
	// ==========================================
	const generateCombinedOptions = () => {
		const combinedOptions = [];
		allServiceClient?.forEach((servico) => {
			const hostsForService =
				allHostsCliente?.filter(
					(host) =>
						host?.servicoId?.toString() ===
						servico?.id_cliente_servico?.toString(),
				) ?? [];

			hostsForService
				.filter((host) => host?.onuItem?.trim())
				.forEach((host) => {
					const splitedOnu = host?.onuItem?.trim()?.split("-");
					combinedOptions.push({
						servico,
						host,
						onu: host,
						label: `${servico?.id_cliente_servico} - ${servico?.nome} | ${host?.host} | ${splitedOnu?.filter(Boolean)?.join(" - ")}`,
					});
				});
		});
		return combinedOptions;
	};

	const handleOnuChange = (index, value) => {
		setServicoHostOnus((prev) => {
			const updated = [...prev];
			updated[index] = value
				? { servico: value?.servico, host: value?.host, onu: value?.onu }
				: { servico: null, host: null, onu: null };
			return updated;
		});
	};

	const handleAddOnu = () =>
		setServicoHostOnus((prev) => [
			...prev,
			{ servico: null, host: null, onu: null },
		]);

	const handleRemoveOnu = (index) =>
		setServicoHostOnus((prev) => prev.filter((_, i) => i !== index));

	// ==========================================
	// ATENDIMENTOS
	// ==========================================
	const getSortedOptions = (options, selected) => {
		const allOption = options?.find((opt) => opt?.id_cliente_servico === "ALL");
		const selectedOptions = options?.filter(
			(opt) =>
				opt?.id_cliente_servico !== "ALL" &&
				selected?.some(
					(s) => s?.id_cliente_servico === opt?.id_cliente_servico,
				),
		);
		const unselectedOptions = options.filter(
			(opt) =>
				opt?.id_cliente_servico !== "ALL" &&
				!selected?.some(
					(s) => s?.id_cliente_servico === opt?.id_cliente_servico,
				),
		);
		return [
			...(allOption ? [allOption] : []),
			...(selectedOptions ?? []),
			...unselectedOptions,
		];
	};

	// ==========================================
	// MOTOR DE REGRAS
	// ==========================================
	const handleAddRule = () => {
		const hasCpe = selectedTypes.includes("CPE");
		const hasOnu = selectedTypes.includes("ONU");

		// Métricas na ordem desejada, com trafego por último
		const orderedMetrics = [
			"latencia",
			"packetLoss",
			"disponibilidade",
			"potencia",
			"trafego",
		];

		const allowedMetrics = orderedMetrics.filter((m) => {
			if (hasCpe && hasOnu) return true;
			if (hasOnu) {
				return m === "potencia" || m === "disponibilidade";
			}
			if (hasCpe) {
				return m !== "potencia";
			}
			return true;
		});

		const available = allowedMetrics.filter(
			(m) => !rules.some((r) => r.metric === m)
		);

		if (available.length === 0) return;

		const nextMetric = available[0];
		const defaultOperator = (nextMetric === "latencia" || nextMetric === "packetLoss") ? "<" : ">";

		setRules((prev) => [
			...prev,
			{
				id: Date.now() + Math.random(),
				metric: nextMetric,
				operator: defaultOperator,
				value: "",
			},
		]);
	};

	const handleRemoveRule = (idToRemove) => {
		setRules((prevRules) => {
			const ruleToRemove = prevRules.find((r) => r.id === idToRemove);
			const newRules = prevRules.filter((rule) => rule.id !== idToRemove);

			if (!ruleToRemove) return newRules;

			return newRules;
		});
	};

	const handleRuleChange = (id, field, newValue) => {
		setRules((prevRules) => {
			const oldRule = prevRules.find((r) => r.id === id);
			const newRules = prevRules.map((rule) =>
				rule.id === id ? { ...rule, [field]: newValue } : rule,
			);

			if (field !== "metric") return newRules;

			setMetrics((prevMetrics) => {
				const updated = { ...prevMetrics };
				if (newValue === "latencia") updated.latencia = true;
				if (newValue === "packetLoss") updated.perdaPacote = true;
				if (newValue === "trafego") updated.trafego = true;
				if (newValue === "disponibilidade") updated.disponibilidade = true;

				return updated;
			});

			return newRules;
		});
	};

	// ==========================================
	// MÉTRICAS
	// ==========================================
	const handleToggleMetric = (metricKey) => {
		setMetrics((prev) => {
			const newState = { ...prev, [metricKey]: !prev[metricKey] };

			// Ao ativar disponibilidade, sincroniza métricas de regras já cadastradas
			if (metricKey === "disponibilidade" && newState.disponibilidade) {
				rules.forEach((rule) => {
					if (rule.metric === "latencia") newState.latencia = true;
					if (rule.metric === "packetLoss") newState.perdaPacote = true;
					if (rule.metric === "trafego") newState.trafego = true;
				});
			}

			return newState;
		});
	};

	// ==========================================
	// HOSTS E INTERFACES (CPE)
	// ==========================================
	const handleHostChange = async (id, hostId) => {
		const interfaces = await getInterface(hostId);
		const host = allHostsCliente?.find((h) => h.hostid === hostId);

		const filteredInterfaces =
			host?.hostsClienteInterfaces?.length > 0
				? interfaces?.filter((i) =>
						host?.hostsClienteInterfaces?.includes(i.name),
					)
				: interfaces;

		setSelectors((prev) =>
			prev.map((selector) =>
				selector.id === id
					? {
							...selector,
							hostId,
							availableInterfaces: filteredInterfaces ?? [],
							interfaces: filteredInterfaces?.map((i) => i.name || i) ?? [],
						}
					: selector,
			),
		);
	};

	const handleInterfaceChange = (id, selectedValues) => {
		setSelectors((prev) =>
			prev.map((selector) => {
				if (selector.id !== id) return selector;

				const allAvailable = selector.availableInterfaces;
				let newSelected = [...selectedValues];

				if (newSelected.includes("Selecionar Todas")) {
					newSelected =
						selector.interfaces.length === allAvailable.length
							? []
							: allAvailable.map((i) => i.name || i);
				}

				newSelected = newSelected.filter((item) => item !== "Selecionar Todas");
				return { ...selector, interfaces: newSelected };
			}),
		);
	};

	const addSelector = () =>
		setSelectors((prev) => [
			...prev,
			{ id: Date.now(), hostId: "", interfaces: [], availableInterfaces: [] },
		]);

	const removeSelector = (id) => {
		if (selectors.length <= 1) return;
		setSelectors((prev) => prev.filter((selector) => selector.id !== id));
	};

	// ==========================================
	// AGENDAMENTO
	// ==========================================
	const obterTextoAgendamento = () => {
		if (!agendamentoDataInicial)
			return "Selecione uma data para calcular o envio";

		const inicio = dayjs(agendamentoDataInicial);

		if (frequenciaAgendamento === "Único")
			return `O envio será realizado em: ${inicio.format("DD/MM/YY HH:mm")}`;

		if (frequenciaAgendamento === "Diário")
			return `O envio será realizado todo dia às ${inicio.format("HH:mm")} a partir de ${inicio.format("DD/MM")}`;

		if (frequenciaAgendamento === "Semanal")
			return `O envio será realizado a cada 7 dias às ${inicio.format("HH:mm")} a partir de ${inicio.format("DD/MM")}`;

		if (frequenciaAgendamento === "Mensal")
			return `O envio será realizado todo dia ${inicio.format("DD")} às ${inicio.format("HH:mm")} a partir de ${inicio.format("DD/MM")}`;

		return "";
	};

	// ==========================================
	// RETORNO DO HOOK
	// ==========================================
	return {
		// estados
		selectedTypes,
		rules,
		selectors,
		allServiceClient,
		loadingServices,
		servicoHostOnus,
		periodoTipo,
		setPeriodoTipo,
		diasPersonalizado,
		setDiasPersonalizado,
		selectedAtendimentosServices,
		setSelectedAtendimentosServices,
		atendimentosInputValue,
		setAtendimentosInputValue,
		atendimentosIsOpen,
		setAtendimentosIsOpen,
		atendimentosSortedOptions,
		setAtendimentosSortedOptions,
		nomeRelatorio,
		setNomeRelatorio,
		dataInicial,
		setDataInicial,
		dataFinal,
		setDataFinal,
		openModal,
		setOpenModal,
		tarifacaoValores,
		setTarifacaoValores,
		metrics,
		isAgendamentoEnabled,
		setIsAgendamentoEnabled,
		agendamentoDataInicial,
		setAgendamentoDataInicial,
		emails,
		frequenciaAgendamento,
		setFrequenciaAgendamento,
		observacoes,
		setObservacoes,
		// computados
		erp,
		labelErp,
		showMetricas,
		showTarifacao,
		showDisponibilidade,
		allSelectedAtendimentos,
		displayedAtendimentosValue,
		// handlers
		handleToggle,
		handleAddEmail,
		handleRemoveEmail,
		handleEmailChange,
		generateCombinedOptions,
		handleOnuChange,
		handleAddOnu,
		handleRemoveOnu,
		getSortedOptions,
		handleAddRule,
		handleRemoveRule,
		handleRuleChange,
		handleToggleMetric,
		handleHostChange,
		handleInterfaceChange,
		addSelector,
		removeSelector,
		obterTextoAgendamento,
	};
}
